import moment from "moment";
import {
  deleteRequest,
  getRequest,
  postRequest,
  downloadWithSavePicker,
} from "~/apis/requests/useApiCall";
import { TableDataWithPagination } from "~/types/generics/tableDataWithPagination";
import { Pagination } from "~/types/pagination";
import { Organization } from "~/projects/crm/types/organization";
import { ProductSummary } from "~/types/productSummary";
import { ElMessageBox } from "element-plus";

export const useOrganizationStore = definePiniaStore("useOrganizationStore", {
  state: () => ({
    _loading: true,
    _tableData: [] as Organization[],
    _selectedOrgForEdit: {} as Organization,
    _filterData: {},
    _simpleFilter: {},
    _allOrganizations: [] as Organization[],
    _simpleOrganizations: [] as Organization[],
    _organization: {} as Organization,
    _productSummary: {} as ProductSummary,
    _pagination: {} as Pagination,
    showEditModal: false,
    showCreateModal: false,
  }),
  getters: {
    tableData: (state) => state._tableData,
    selectedOrgForEdit: (state) => state._selectedOrgForEdit,
    pagination: (state) => state._pagination,
    allOrganizations: (state) => state._allOrganizations,
    industryList: () => [
      {
        value: "SME",
        label: "SME",
      },
      {
        value: "IT",
        label: "IT",
      },
      {
        value: "Construction",
        label: "Construction",
      },
      {
        value: "Agriculture",
        label: "Agriculture",
      },
      {
        value: "Financial Services",
        label: "Financial Services",
      },
      {
        value: "Banking",
        label: "Banking",
      },
      {
        value: "Healthcare",
        label: "Healthcare",
      },
      {
        value: "Education",
        label: "Education",
      },
      {
        value: "Hospitality",
        label: "Hospitality",
      },
      {
        value: "Apparel & Textile",
        label: "Apparel & Textile",
      },
      {
        value: "Retail",
        label: "Retail",
      },
      {
        value: "Manufacturing",
        label: "Manufacturing",
      },
      {
        value: "FMCG",
        label: "FMCG",
      },
      {
        value: "Telecommunications",
        label: "Telecommunications",
      },
      {
        value: "Ride-Sharing / Ride-Hailing",
        label: "Ride-Sharing / Ride-Hailing",
      },
      {
        value: "Media & Entertainment",
        label: "Media & Entertainment",
      },
      {
        value: "Fashion & Jewelry",
        label: "Fashion & Jewelry",
      },
      {
        value: "BPO",
        label: "BPO",
      },
      {
        value: "Logistics",
        label: "Logistics",
      },
      {
        value: "E-commerce",
        label: "E-commerce",
      },
      {
        value: "Government",
        label: "Government",
      },
      {
        value: "Pharmaceuticals",
        label: "Pharmaceuticals",
      },
      {
        value: "Automobile",
        label: "Automobile",
      },
      {
        value: "Food & Beverage",
        label: "Food & Beverage",
      },
    ],
    productSummary: (state) => state._productSummary,
    organization: (state) => state._organization,
    simpleOrganizations: (state) => state._simpleOrganizations,
  },
  actions: {
    async getOrganizations(pageNo = 1, sortBy = "id", ascending = false) {
      this._loading = true;
      const sortDir = ascending ? "asc" : "desc";
      let url = `crm/organizations?pagination=true&page=${pageNo}&sort_column=${sortBy}&dir=${sortDir}`;
      for (const key in this._filterData) {
        // @ts-ignore
        url += `&${key}=${this._filterData[key]}`;
      }

      const { response, error } = await getRequest<
        TableDataWithPagination<Organization>
      >(url);
      if (!error) {
        const { data, ...pagination } = response.data;
        this.$state._tableData = data;
        this.$state._pagination = pagination;
        this._loading = false;
      }
    },
    async exportTable() {
      let url = `crm/organizations/export?`;
      for (const [key, value] of Object.entries(this._filterData)) {
        if (value === null) continue;
        url += `${key}=${value}&`;
      }

      const date = moment().format("YYYY-MM-DD");
      const fileName = `${"organizations-report"}-${date}`;

      await downloadWithSavePicker(url, fileName);
    },
    async createOrganization(organization: any) {
      const { response, error } = await postRequest<any>(
        "crm/organizations",
        organization,
        true,

        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (error) {
        ElMessageBox({
          title: "Organization creation failed",
          message: error?.response?.data.message,
          type: "error",
        });
        return;
      }
      return response;
    },
    async updateOrganization(id: number, organization: any) {
      const { response, error } = await postRequest<any>(
        `crm/organizations/${id}`,
        organization,
        true,

        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (error) {
        const errors = error.response?.data?.errors || {};

        let formattedErrors = `<ul style="list-style-type: disc; padding-left: 20px;">`;

        Object.keys(errors).forEach((key) => {
          errors[key].forEach((message) => {
            formattedErrors += `<li>${message}</li>`;
          });
        });

        formattedErrors += "</ul>";
        ElMessageBox({
          title: "Organization update failed",
          message: formattedErrors || error.message,
          dangerouslyUseHTMLString: true,
          type: "info",
        });
        return;
      }
      return response;
    },
    async deleteOrganization(organization: Organization) {
      return await deleteRequest(`crm/organizations/${organization.id}`);
    },
    async getAllOrganizations(category_id?: number) {
      const url = category_id
        ? `crm/organizations?category_id=${category_id}`
        : `crm/organizations`;
      const { response, error } = await getRequest<Organization[]>(url);
      if (!error) {
        this.$state._allOrganizations = response.data;
      }
    },
    async getSimpleOrganizations() {
      let url = `crm/organizations/simple?`;
      for (const key in this._simpleFilter) {
        // @ts-ignore
        url += `${key}=${this._simpleFilter[key]}&`;
      }
      const { response, error } = await getRequest<Organization[]>(url);
      if (!error) {
        this.$state._simpleOrganizations = response.data;
      }
    },
    async getOrganization(id: number) {
      const { response, error } = await getRequest<Organization>(
        `crm/organizations/${id}`
      );
      if (!error) {
        this.$state._organization = response.data;
      }
    },

    async getProductSummaryForOrg(orgId: number) {
      const { response, error } = await getRequest<ProductSummary>(
        `crm/organizations/${orgId}/summary`
      );
      if (!error) {
        this.$state._productSummary = response.data;
      }
    },

    // mutations
    setCreateModalVisibility(val: boolean) {
      this.$state.showCreateModal = val;
    },
    setEditModalVisibility(val: boolean) {
      this.$state.showEditModal = val;
    },
    setEditData(organization: Organization) {
      this.$state._selectedOrgForEdit = organization;
    },
    setFilterData(filter: any) {
      this.$state._filterData = filter;
    },
    setSimpleFilter(filter: any) {
      this.$state._simpleFilter = filter;
    },
  },
});
